import React, { useState, useEffect, useRef } from 'react';
import './AutocompleteDropdown.css';

const AutocompleteDropdown = ({
  // Required props
  fetchFunction,      // Function to fetch data (searchTerm, token) => Promise<Array>
  displayFunction,    // Function to format display text (item) => string
  keyFunction,        // Function to get unique key (item) => string
  selectedItem,       // Currently selected item
  onItemSelect,       // Callback when item is selected (item) => void
  
  // Optional props
  placeholder = "Buscar...",
  searchPlaceholder = "Buscar...",
  disabled = false,
  loading = false,
  error = null,
  icon = "fas fa-search",
  maxResults = 10,
  minSearchLength = 0,
  debounceDelay = 300,
  showClearButton = true,
  emptyMessage = "No se encontraron resultados",
  loadingMessage = "Cargando...",
  renderCustomItem = null,  // Custom render function for items (item, isSelected) => ReactNode
  renderSelectedPreview = null, // Custom render function for selected item preview (item) => ReactNode
  renderEmptyState = null,  // Custom render function for empty state (searchTerm) => ReactNode
  renderAlwaysVisible = null, // Custom render function for always visible item (searchTerm) => ReactNode
  className = '',
  
  // Advanced props for API integration
  token = null,       // Authentication token
  initialData = [],   // Initial data to show (before search)
  clearOnSelect = false, // Whether to clear search after selection
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState(initialData || []);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isLoading, setIsLoading] = useState(loading || false);
  const [searchError, setSearchError] = useState(error);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [lastSearchTerm, setLastSearchTerm] = useState('');
  
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const debounceRef = useRef(null);
  const requestInProgressRef = useRef(false);
  
  // Store frequently changing props in refs to avoid dependency issues
  const fetchFunctionRef = useRef(fetchFunction);
  const tokenRef = useRef(token);
  const minSearchLengthRef = useRef(minSearchLength);
  const debounceDelayRef = useRef(debounceDelay);
  const initialDataRef = useRef(initialData);

  // Update refs when props change
  useEffect(() => {
    fetchFunctionRef.current = fetchFunction;
    tokenRef.current = token;
    minSearchLengthRef.current = minSearchLength;
    debounceDelayRef.current = debounceDelay;
    initialDataRef.current = initialData;
  });

  // Search effect with minimal dependencies to prevent loops
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Only proceed if search term actually changed
    if (searchTerm === lastSearchTerm) {
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(async () => {
      // Double-check the search term hasn't changed during debounce
      if (searchTerm !== lastSearchTerm) {
        setLastSearchTerm(searchTerm);
        
        if (searchTerm.length >= minSearchLengthRef.current && fetchFunctionRef.current) {
          // Prevent multiple simultaneous requests
          if (requestInProgressRef.current) {
            return;
          }

          try {
            requestInProgressRef.current = true;
            setIsLoading(true);
            setSearchError(null);
            const results = await fetchFunctionRef.current(searchTerm, tokenRef.current);
            setItems(Array.isArray(results) ? results : []);
          } catch (err) {
            console.error('Error fetching data:', err);
            setSearchError(err.message || 'Error al cargar datos');
            setItems([]);
          } finally {
            setIsLoading(false);
            requestInProgressRef.current = false;
          }
        } else if (searchTerm.length === 0) {
          // When search is empty, show initial data or empty array
          setItems(initialDataRef.current || []);
          setIsLoading(false);
        } else {
          // Search term too short
          setItems([]);
          setIsLoading(false);
        }
      }
    }, debounceDelayRef.current);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, isOpen, lastSearchTerm]); // Now includes lastSearchTerm to fix the dependency

  // Filter and limit items
  useEffect(() => {
    // Just limit results, don't do additional filtering since the fetch function should handle filtering
    const filtered = items.slice(0, maxResults);
    setFilteredItems(filtered);
    setHighlightedIndex(-1);
  }, [items, maxResults]);

  // Position dropdown function
  const positionDropdown = () => {
    if (triggerRef.current && menuRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const menu = menuRef.current;
      
      menu.style.position = 'fixed';
      menu.style.top = `${triggerRect.bottom + 4}px`;
      menu.style.left = `${triggerRect.left}px`;
      menu.style.width = `${triggerRect.width}px`;
      menu.style.maxWidth = `${triggerRect.width}px`;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        // Cancel any pending requests when closing
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        requestInProgressRef.current = false;
        
        setIsOpen(false);
        setSearchTerm('');
        setLastSearchTerm('');
        // Reset items to clear previous results
        setItems(initialDataRef.current || []);
      }
    };

    // Use a longer delay to ensure the toggle event completes first
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens and position dropdown
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
        positionDropdown();
      }, 0);
      
      // Reposition on scroll/resize
      const handleReposition = () => {
        if (isOpen) {
          positionDropdown();
        }
      };
      
      window.addEventListener('scroll', handleReposition, true);
      window.addEventListener('resize', handleReposition);
      
      return () => {
        window.removeEventListener('scroll', handleReposition, true);
        window.removeEventListener('resize', handleReposition);
      };
    }
  }, [isOpen]);

  const handleToggleDropdown = () => {
    if (!disabled) {
      if (isOpen) {
        // Closing dropdown - cancel any pending requests and reset items
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        requestInProgressRef.current = false;
        setItems(initialDataRef.current || []); // Reset items to clear previous results
      }
      
      setIsOpen(!isOpen);
      if (!isOpen) {
        // Opening dropdown - reset search
        setSearchTerm('');
        setLastSearchTerm('');
        setHighlightedIndex(-1);
        setItems(initialDataRef.current || []); // Start with initial data
      }
    }
  };

  const handleItemSelect = (item) => {
    onItemSelect(item);
    setIsOpen(false);
    if (clearOnSelect) {
      setSearchTerm('');
    }
    setHighlightedIndex(-1);
  };

  const handleClearSelection = (e) => {
    e.stopPropagation();
    onItemSelect(null);
    setSearchTerm('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = (e) => {
    switch (e.key) {
      case 'Escape':
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        requestInProgressRef.current = false;
        setIsOpen(false);
        setSearchTerm('');
        setLastSearchTerm('');
        setHighlightedIndex(-1);
        setItems(initialDataRef.current || []); // Reset items when escaping
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredItems[highlightedIndex]) {
          handleItemSelect(filteredItems[highlightedIndex]);
        } else if (filteredItems.length > 0) {
          handleItemSelect(filteredItems[0]);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredItems.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredItems.length - 1
        );
        break;
      default:
        break;
    }
  };

  const getDisplayText = () => {
    if (selectedItem && displayFunction) {
      return displayFunction(selectedItem);
    }
    return placeholder;
  };

  const renderDefaultItem = (item, isSelected, isHighlighted) => (
    <div className={`autocomplete-item ${isSelected ? 'selected' : ''} ${isHighlighted ? 'highlighted' : ''}`}>
      <div className="item-content">
        <span className="item-text">{displayFunction ? displayFunction(item) : String(item)}</span>
      </div>
      {isSelected && (
        <i className="fas fa-check selected-icon"></i>
      )}
    </div>
  );

  const renderItem = (item, index) => {
    const isSelected = selectedItem && keyFunction && 
      keyFunction(selectedItem) === keyFunction(item);
    const isHighlighted = index === highlightedIndex;
    const key = keyFunction ? keyFunction(item) : index;

    const content = renderCustomItem 
      ? renderCustomItem(item, isSelected, isHighlighted)
      : renderDefaultItem(item, isSelected, isHighlighted);

    return (
      <div
        key={key}
        className={`autocomplete-item-wrapper ${isHighlighted ? 'highlighted' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          handleItemSelect(item);
        }}
        onMouseEnter={() => setHighlightedIndex(index)}
      >
        {content}
      </div>
    );
  };

  return (
    <div className={`autocomplete-dropdown ${disabled ? 'disabled' : ''} ${className}`} ref={dropdownRef}>
      {isOpen ? (
        // Show search input as the main element when open
        <div className="search-mode" ref={triggerRef}>
          <input
            ref={searchInputRef}
            type="text"
            className="search-input-main"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
          <i className={`fas fa-chevron-down dropdown-arrow ${isOpen ? 'open' : ''}`}></i>
        </div>
      ) : (
        // Show dropdown trigger when closed
        <div 
          ref={triggerRef}
          className={`dropdown-trigger ${selectedItem ? 'has-selection' : ''}`}
          onClick={handleToggleDropdown}
        >
          <div className="selected-value">
            {icon && <i className={icon}></i>}
            <span className={selectedItem ? 'selected' : 'placeholder'}>
              {renderSelectedPreview && selectedItem 
                ? renderSelectedPreview(selectedItem)
                : getDisplayText()
              }
            </span>
          </div>
          <div className="dropdown-actions">
            {showClearButton && selectedItem && (
              <button
                type="button"
                className="clear-btn"
                onClick={handleClearSelection}
                title="Limpiar selección"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
            <i className="fas fa-chevron-down dropdown-arrow"></i>
          </div>
        </div>
      )}

      {isOpen && (
        <div 
          ref={menuRef}
          className="dropdown-menu"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="items-list">
            {isLoading ? (
              <div className="loading-item">
                <i className="fas fa-spinner fa-spin"></i>
                <span>{loadingMessage}</span>
              </div>
            ) : searchError ? (
              <div className="error-item">
                <i className="fas fa-exclamation-triangle"></i>
                <span>{searchError}</span>
              </div>
            ) : searchTerm.length <= 1 ? (
              <div className="loading-item">
                <i className="fas fa-keyboard"></i>
                <span>Empiece a escribir para iniciar la búsqueda</span>
              </div>
            ) : (
              <>
                {filteredItems.length === 0 ? (
                  renderEmptyState ? renderEmptyState(searchTerm) : (
                    <div className="empty-item">
                      <i className="fas fa-search"></i>
                      <span>{emptyMessage}</span>
                    </div>
                  )
                ) : (
                  filteredItems.map((item, index) => renderItem(item, index))
                )}
                
                {/* Always visible item - shown regardless of search results */}
                {renderAlwaysVisible && (
                  <div className="always-visible-item">
                    {renderAlwaysVisible(searchTerm, () => setIsOpen(false))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AutocompleteDropdown;
